import type { ApolloClient } from '@apollo/client';
import { ME_QUERY, type MeQueryResult } from '@scio3/graphql-client';
import type { MeResponse } from '@scio3/shared-types';

/**
 * Bug 6 — referral expansion prompt only appeared on refresh, not right after
 * pay→Continue.
 *
 * Root cause: the referral widget reads `me.referralPromptPending` from the
 * Zustand auth-store, but `AuthProvider` populates that store via one-off
 * `client.query()` calls (no live observable). The charge mutation's
 * `refetchQueries: ['Me']` updates the Apollo *cache* but never re-runs
 * `setMe()`, so the store mirror stays stale until a reload / window-focus
 * re-sync. open-api also only sets `referralPromptPending` from the async
 * Stripe `invoice.paid` webhook, so it may not be present the instant the
 * post-pay Continue is clicked.
 *
 * Fix (mirrors `PaymentMethodPage`'s manual store re-sync after card save):
 * after a prompt-eligible finish, re-run ME_QUERY (`network-only`) and push the
 * fresh `me` into the auth-store via `setMe`. If the prompt isn't present yet
 * (webhook latency), retry a few times over a short, bounded window and stop as
 * soon as it appears or the attempt cap is hit. Cancellable so the dialog
 * closing / unmounting aborts any in-flight poll.
 */

/** Default bounded-poll shape: ~3 attempts over ~5s total. */
export const REFERRAL_PROMPT_SYNC = {
  /** Total ME_QUERY attempts, including the immediate first one. */
  maxAttempts: 3,
  /** Delay between attempts (ms). 2 gaps × 2500ms ≈ 5s total window. */
  retryDelayMs: 2500,
} as const;

export interface SyncMeForReferralPromptDeps {
  /** Apollo client used to re-run ME_QUERY against the network. */
  apolloClient: Pick<ApolloClient, 'query'>;
  /** auth-store setter that refreshes the local `me` mirror the widget reads. */
  setMe: (me: MeResponse) => void;
  /**
   * Reads the CURRENT pending prompt from the live auth-store
   * (`useAuthStore.getState().me?.referralPromptPending`). Called after each
   * sync to decide whether to keep polling.
   */
  getPendingPrompt: () => string | undefined;
}

export interface SyncMeForReferralPromptOptions {
  maxAttempts?: number;
  retryDelayMs?: number;
}

/**
 * Re-sync `me` into the auth-store, then bounded-poll until the referral prompt
 * appears or the attempt cap is reached. Returns a cancel function that aborts
 * any pending retry (call it on dialog change / unmount). Idempotent w.r.t. the
 * widget's optimistic clear in `useReferralClient` — the poll simply stops once
 * a non-empty pending value is observed.
 */
export function syncMeForReferralPrompt(
  deps: SyncMeForReferralPromptDeps,
  options: SyncMeForReferralPromptOptions = {},
): () => void {
  const { apolloClient, setMe, getPendingPrompt } = deps;
  const maxAttempts = options.maxAttempts ?? REFERRAL_PROMPT_SYNC.maxAttempts;
  const retryDelayMs = options.retryDelayMs ?? REFERRAL_PROMPT_SYNC.retryDelayMs;

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const runAttempt = async (attempt: number): Promise<void> => {
    if (cancelled) return;
    try {
      const result = await apolloClient.query<MeQueryResult>({
        query: ME_QUERY,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });
      if (cancelled) return;
      if (result.data?.me) {
        setMe(result.data.me);
      }
    } catch {
      // Non-fatal — the prompt also surfaces on the next window-focus re-sync.
    }
    if (cancelled) return;
    // Stop as soon as the prompt is present, or when we've exhausted attempts.
    if (getPendingPrompt() || attempt + 1 >= maxAttempts) return;
    timer = setTimeout(() => {
      timer = null;
      void runAttempt(attempt + 1);
    }, retryDelayMs);
  };

  void runAttempt(0);
  return cancel;
}
